import moment from "moment";
import VendorUnit from "../VendorUnit";
import RatesByDate from "./DateRate";
import EnumProductRateType from "./EnumProductRateType";
import { isStringEmpty } from "../../_helpers/Functions";

export function getUnitRate(rates: RatesByDate[], unitName: string | null, numberUnits: number, getFirstIfNull = false) {
  const unitRates = rates.flatMap(a => a.unitRates).filter(b => b.vendorUnitDisplayName === unitName).flatMap(a => a.rates).filter(c => c.rateType == EnumProductRateType.UnitQtyRange);

  // Find rate matching the selected number of units.
  unitRates.sort((a, b) => {
    // Sort by desc start unit.
    return b.startUnitNumber - a.endUnitNumber
  });

  let unitRate = unitRates.find(r => r.startUnitNumber <= numberUnits // Minimum units.
    && (r.endUnitNumber >= numberUnits || r.endUnitNumber === 1)); // Max units or 1-to-1.

  if (!unitRate && getFirstIfNull) {
    unitRate = unitRates.length > 0 ? unitRates[0] : undefined;
  }

  return unitRate;
}

export function getHourlyRate(rates: RatesByDate[], unitName: string | null, selectedStartTime: string | null, selectedEndTime: string | null, getFirstIfNull = false) {
  const hourlyRates = rates.flatMap(a => a.unitRates).filter(b => b.vendorUnitDisplayName === unitName).flatMap(a => a.rates).filter(c => c.rateType == EnumProductRateType.HoursPerUnit);


  // Find rate matching the selected number of hours.
  hourlyRates.sort((a, b) => {
    // Sort by desc start hour.
    return b.startHour - a.startHour
  });

  selectedStartTime = selectedStartTime !== null ? selectedStartTime : "";
  selectedEndTime = selectedEndTime !== null ? selectedEndTime : "";

  let numberOfHours: number = moment.duration(moment(selectedEndTime, 'HH:mm').diff(moment(selectedStartTime, 'HH:mm'))).asHours();

  if (numberOfHours < 0) {
    //only 0-24 hours
    numberOfHours = moment.duration(moment(selectedEndTime, 'HH:mm').add(1, "day").diff(moment(selectedStartTime, 'HH:mm'))).asHours();
  }

  let hourlyRate = hourlyRates.find(r => r.startHour <= numberOfHours // Minimum hours.
    && (r.endHour >= numberOfHours || r.endHour === 1)); // Max hours or 1-to-1.

  if (!hourlyRate && getFirstIfNull) {
    hourlyRate = hourlyRates.length > 0 ? hourlyRates[0] : undefined;
  }

  return hourlyRate;
}

export function getDailyRate(rates: RatesByDate[], unitName: string | null, startDate: string | null, endDate: string | null, selectedStartTime: string | undefined, selectedEndTime: string | undefined, getFirstIfNull = false) {
  const dayRates = rates.filter(a => a.unitRates.findIndex(b => b.vendorUnitDisplayName === unitName && b.rates.findIndex(c => c.rateType == EnumProductRateType.DayRange || c.rateType == null) >= 0) >= 0);
  // console.log("day rates", dayRates);

  // Each ProductRate is a date. Rate Days n-to-n is applied individually to each date, based on the amount of selected days.

  startDate = startDate !== null ? startDate : "";
  endDate = endDate !== null ? endDate : "";
  selectedStartTime = selectedStartTime !== null ? selectedStartTime : "";
  selectedEndTime = selectedEndTime !== null ? selectedEndTime : "";

  // Find rate matching selected days amount.
  const selectedStartDate = moment(startDate);
  const selectedEndDate = moment(endDate);

  let numberOfDays = moment.duration(selectedEndDate.diff(selectedStartDate)).asDays() + 1;
  const numberOfHours: number = moment.duration(moment(selectedEndTime, 'HH:mm').diff(moment(selectedStartTime, 'HH:mm'))).asHours();

  // console.log("num days", numberOfDays);
  // console.log("num hours", numberOfHours);

  let days: Date[] = [];
  let day = moment(startDate);
  const lastDate = moment(endDate);
  while (day.isSameOrBefore(lastDate)) {
    days.push(day.toDate());
    day = day.clone().add(1, 'day');
  }

  let ratesForDays = dayRates.filter((rate: any) => days.find(day => moment(day).isSame(moment(rate.date))));

  ratesForDays = ratesForDays.filter((rd: RatesByDate) => rd.unitRates.filter(ur => ur.rates.length).length);

  let ratesForNumberOfDays = [...ratesForDays];
  let poppedRateByDate: (RatesByDate | undefined) = undefined;
  if (numberOfHours <= 0) {
    numberOfDays -= 1; //full 0-24 hours = 1 day
    poppedRateByDate = ratesForNumberOfDays.pop(); //remove last day rate
  }

  if (getFirstIfNull && numberOfDays == 0) {
    numberOfDays = 1;
    if (poppedRateByDate) {
      ratesForNumberOfDays.push(poppedRateByDate);
    }
  }

  let noRateMessage = "";

  let productRates = ratesForNumberOfDays.map(rd => {
    // Select the UnitRate for the numberOfDays.

    const rateForNumberOfDays = rd.unitRates.filter(a => a.vendorUnitDisplayName === unitName).flatMap(a => a.rates).sort((a, b) => {
      return b.startDay - a.startDay; // Sort by desc start day.
    }).find(r => { // Find the rate for the numberOfDays.
      return r.startDay <= numberOfDays // Minimum days.
        && (r.endDay >= numberOfDays || r.endDay === 1) // Max days or 1-to-1.
    })

    if (!rateForNumberOfDays) {
      noRateMessage += ` ${moment(rd.date).format('MM/DD/YYYY')} No rates available.`;
    }

    return rateForNumberOfDays;
  });

  if ((productRates.length == 0 || productRates.findIndex(a => a != null) < 0) && getFirstIfNull) {
    productRates = ratesForNumberOfDays.length > 0 ? ratesForNumberOfDays[0].unitRates[0].rates : [];
  }

  return {
    productRates,
    noRateMessage
  };
}

// export function getAvailableVendorUnits(rates: DateRate[]) {
//   let vendorUnits: VendorUnit[] = [];
//   rates.forEach(v => {
//     v.rates.forEach(r => {
//       if (vendorUnits.findIndex(vu => vu.uniqueIdForSelection == r.vendorUnitDisplayName) < 0) {
//         vendorUnits.push(new VendorUnit({ uniqueIdForSelection: r.vendorUnitDisplayName, unitName: r.vendorUnitDisplayName }));
//       }
//     })
//   });
//   return vendorUnits;
// }

export function getVendorUnitIdsFromSelectedName(rates: RatesByDate[], selectedName: string | null, startDate: string | null = null, endDate: string | null = null) {
  if (startDate === null || selectedName === null) return [];
  if (endDate === null) endDate = startDate;

  const start = moment(startDate);
  const end = moment(endDate);
  const ids: Number[] = [];

  rates.forEach(dayRate => {
    const date = moment(dayRate.date);

    if (date.isSameOrAfter(start) && date.isSameOrBefore(end)) {
      dayRate.unitRates.forEach(rate => {
        if (ids.findIndex(id => id === rate.vendorUnitId) < 0 && rate.vendorUnitDisplayName === selectedName) {
          ids.push(rate.vendorUnitId);
        }
      });
    }
  });

  return ids;
}

// export function getAvailableVendorUnits(units: VendorUnit[], rates: RatesByDate[], startDate: string | null = null, endDate: string | null = null) {
//   if (startDate === null)
//     return [];

//   let start = moment(startDate);
//   let end = moment(endDate);
//   let vendorUnits: VendorUnit[] = [];
//   rates.forEach(v => {
//     let date = moment(v.date);
//     //12/20/21
//     //only show Units based on the startDate
//     if (date.isSame(start)) { //only selected 1 date
//       v.unitRates.forEach(r => {
//         if (vendorUnits.findIndex(vu => vu.uniqueIdForSelection.trim() == r.vendorUnitDisplayName.trim()) < 0) {

//           const vendUnit = new VendorUnit({ uniqueIdForSelection: r.vendorUnitDisplayName.trim(), unitName: r.vendorUnitDisplayName.trim(), vendorUnitId: r.vendorUnitId, isLive: r.rates.some(ur => ur.isLive), rates: r.rates, });
//           vendorUnits.push(vendUnit);
//         }
//       })
//     }
//   });
//   return vendorUnits;
// }

export function getAvailableVendorUnits(vendorUnits: VendorUnit[], dateRates: RatesByDate[], startDate: string | null = null, endDate: string | null = null) {
  if (startDate === null)
    return [];

  let start = moment(startDate);
  // let end = moment(endDate);
  let units: VendorUnit[] = [];
  dateRates.forEach(dateRate => {
    let date = moment(dateRate.date);
    //12/20/21
    //only show Units based on the startDate
    if (date.isSame(start)) { //only select 1 date
      dateRate.unitRates.forEach(unitRate => {
        if (units.findIndex(vu => vu.uniqueIdForSelection.trim() === unitRate.vendorUnitDisplayName.trim()) < 0) {
          const unit = vendorUnits.find(u => u.vendorUnitId === unitRate.vendorUnitId);
          
          if (!!unit)
            units.push(new VendorUnit({
              ...unit,
              // this name shit needs to be reworked
              uniqueIdForSelection: unitRate.vendorUnitDisplayName.trim(),
              unitName: unitRate.vendorUnitDisplayName.trim(),
              // vendorUnitId: unitRate.vendorUnitId, 
              isLive: unitRate.rates.some(ur => ur.isLive),
              rates: unitRate.rates,
              // personPerUnit: unit.personPerUnit, 
              unitDescription: unit.unitDescription,
              // productRateId: unit.productRateId
              published: !unitRate.rates.some(r => !r.published)
            }));
        }
      })
    }
  });
  return units;
}

export function getWeekIdAndRateType(weekId: any, rateType: any) {
  return weekId + "-" + rateType;
}

export function getRateTypeFromUnitName(rates: RatesByDate[], name: string | null, date: string | null = null) {
  let rateType = 0;

  rates.forEach(v => {
    if (isStringEmpty(date) || moment(date).isSame(moment(v.date))) {
      v.unitRates.forEach(r => {
        if (r.vendorUnitDisplayName === name) {
          rateType = r.rates[0].rateType;
        }
      })
    }
  });
  return rateType;
}

export function getVendorUnitIdFromUnitName(rates: RatesByDate[], name: string | null, date: string | null = null) {
  let vendorUnitId = 0;
  rates.forEach(v => {
    if (isStringEmpty(date) || moment(date).isSame(moment(v.date))) {
      v.unitRates.forEach(r => {
        if (r.vendorUnitDisplayName === name) {
          vendorUnitId = r.vendorUnitId;
        }
      })
    }
  });
  return vendorUnitId;
}

export function getUnitNameFromVendorUnitId(rates: RatesByDate[], selectedUnit: number) {
  let name = "";
  if (selectedUnit && rates) {
    rates.forEach(v => {
      v.unitRates.forEach(r => {
        if (r.vendorUnitId === selectedUnit) {
          name = r.vendorUnitDisplayName;
        }
      })
    });
  }
  return name;
}

export function populateTimes() {
  let start = moment().startOf('day');
  let end = moment().endOf('day');
  start.hours(Math.ceil(start.hours() / 1) * 1);
  let result: any[] = [];
  let current = moment(start);
  while (current <= end) {
    result.push({
      value: current.format('HH:mm'),
      label: current.format('hh:mm a'),
    });
    current.add(1, 'hour');
  }
  return result;
}